import { useEffect, useState } from 'react'

import { useRenderTimeMs } from '@/helpers/ssr/render-time'

export interface UseRenderNowMsOptions {
  /**
   * Keep the first client render pinned to the server render time, then switch
   * to the real client clock after mount.
   */
  live?: boolean
  /** Optional refresh interval once live client updates start. */
  refreshMs?: number
}

interface RenderClockState {
  renderedAtMs: number | null
  liveNowMs: number | null
}

// False until the first live clock tick runs on the client. The null first
// render exists to keep hydration quiet (server markup has no client time),
// but that only matters once per page load — replaying it on every remount
// makes consumers (e.g. the sidebar rows' timestamp line) collapse for a
// frame and reflow the layout on each mount.
let hasClientClockStarted = false

export const useRenderNowMs = ({
  live = true,
  refreshMs,
}: UseRenderNowMsOptions = {}): number | null => {
  const renderedAtMs = useRenderTimeMs()
  const [clockState, setClockState] = useState<RenderClockState>(() => ({
    renderedAtMs,
    liveNowMs: live && hasClientClockStarted ? Date.now() : null,
  }))

  // When navigation brings in a new SSR/ISR timestamp, prefer that value
  // until the post-mount live clock effect catches up.
  const renderedAtChanged = clockState.renderedAtMs !== renderedAtMs

  useEffect(() => {
    if (!live) {
      setClockState({
        renderedAtMs,
        liveNowMs: null,
      })
      return
    }

    const update = () => {
      hasClientClockStarted = true
      setClockState({
        renderedAtMs,
        liveNowMs: Date.now(),
      })
    }

    update()

    if (!refreshMs) return

    const intervalId = window.setInterval(update, refreshMs)
    return () => window.clearInterval(intervalId)
  }, [live, refreshMs, renderedAtMs])

  if (renderedAtChanged) {
    return renderedAtMs
  }

  return clockState.liveNowMs ?? renderedAtMs
}
